import { useEffect, useState, useCallback, useRef } from "react";
import { Upload, Trash2, Download, FileText, Image as ImgIcon, Music, Video, File as FileIcon, Lock, Sparkles, Pencil, Check, X } from "lucide-react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

function fmtBytes(n) {
  if (!n) return "0 B";
  const u = ["B", "KB", "MB", "GB"]; let i = 0;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(1)} ${u[i]}`;
}

function iconFor(ct) {
  if (!ct) return FileIcon;
  if (ct.startsWith("image/")) return ImgIcon;
  if (ct.startsWith("audio/")) return Music;
  if (ct.startsWith("video/")) return Video;
  if (ct.includes("pdf") || ct.startsWith("text/") || ct.includes("word") || ct.includes("officedocument")) return FileText;
  return FileIcon;
}

// Builds a thumbnail URL. Includes ?auth=<token> from localStorage as a fallback
// for mobile browsers that drop the session cookie cross-context.
function thumbUrl(materialId) {
  const token = (typeof window !== "undefined") ? localStorage.getItem("studict_session_token") : null;
  const base = `${API_BASE}/api/materials/${materialId}/download`;
  return token ? `${base}?auth=${encodeURIComponent(token)}` : base;
}

function MaterialCard({ m, onDownload, onDelete, onRename }) {
  const Icon = iconFor(m.content_type);
  const isImage = (m.content_type || "").startsWith("image/");
  const isVideo = (m.content_type || "").startsWith("video/");
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(m.name);
  const [busy, setBusy] = useState(false);
  const [imgFailed, setImgFailed] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { if (editing && inputRef.current) inputRef.current.select(); }, [editing]);

  const commit = async () => {
    const next = draft.trim();
    if (!next || next === m.name) { setEditing(false); setDraft(m.name); return; }
    setBusy(true);
    try {
      await onRename(m.id, next);
      setEditing(false);
    } catch { /* parent shows toast */ }
    finally { setBusy(false); }
  };

  const cancel = () => { setEditing(false); setDraft(m.name); };

  return (
    <article className="nb-card p-4" data-testid={`material-card-${m.name}`}>
      {/* Preview */}
      <div className="relative aspect-video w-full bg-slate-100 border-2 border-slate-900 rounded-md overflow-hidden mb-3" data-testid={`material-preview-${m.id}`}>
        {isImage && !imgFailed ? (
          <img
            src={thumbUrl(m.id)}
            alt={m.name}
            loading="lazy"
            onError={() => setImgFailed(true)}
            className="w-full h-full object-cover"
          />
        ) : isVideo && !imgFailed ? (
          <video
            src={thumbUrl(m.id)}
            preload="metadata"
            onError={() => setImgFailed(true)}
            className="w-full h-full object-cover"
            muted
          />
        ) : (
          <div className="w-full h-full grid place-items-center">
            <Icon className="w-10 h-10 text-slate-500" strokeWidth={2}/>
          </div>
        )}
      </div>

      {/* Name + inline rename */}
      {editing ? (
        <div className="flex items-center gap-1.5">
          <input
            ref={inputRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") commit(); else if (e.key === "Escape") cancel(); }}
            disabled={busy}
            data-testid={`material-rename-input-${m.id}`}
            className="nb-input flex-1 font-display font-bold text-base !py-1.5"
          />
          <button onClick={commit} disabled={busy} data-testid={`material-rename-save-${m.id}`} className="p-1.5 border-2 border-slate-900 rounded-md bg-yellow-300" title="Save">
            <Check className="w-3.5 h-3.5" strokeWidth={2.5}/>
          </button>
          <button onClick={cancel} disabled={busy} data-testid={`material-rename-cancel-${m.id}`} className="p-1.5 border-2 border-slate-900 rounded-md bg-white" title="Cancel">
            <X className="w-3.5 h-3.5" strokeWidth={2.5}/>
          </button>
        </div>
      ) : (
        <div className="flex items-start gap-2">
          <h3 className="font-display text-base font-bold truncate flex-1 min-w-0" title={m.name}>{m.name}</h3>
          <button
            onClick={() => setEditing(true)}
            data-testid={`material-rename-btn-${m.id}`}
            aria-label="Rename"
            title="Rename"
            className="shrink-0 p-1.5 border-2 border-slate-900 rounded-md bg-white hover:bg-yellow-100"
          >
            <Pencil className="w-3.5 h-3.5" strokeWidth={2.5}/>
          </button>
        </div>
      )}

      <p className="text-xs text-slate-600 mt-1">{fmtBytes(m.size)} · {m.content_type?.split("/")[1] || "file"}</p>

      <div className="mt-3 flex gap-2">
        <button onClick={() => onDownload(m)} data-testid={`material-open-${m.id}`} className="nb-btn-secondary text-xs inline-flex items-center gap-1 py-2 px-3">
          <Download className="w-3 h-3" strokeWidth={2.5}/> Open
        </button>
        <button onClick={() => onDelete(m.id)} data-testid={`material-delete-${m.name}`} className="nb-btn-danger text-xs inline-flex items-center gap-1 py-2 px-3">
          <Trash2 className="w-3 h-3" strokeWidth={2.5}/>
        </button>
      </div>
    </article>
  );
}

export default function Materials() {
  const [items, setItems] = useState([]);
  const [quota, setQuota] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    const [m, q] = await Promise.all([api.get("/materials"), api.get("/storage/quota")]);
    setItems(m.data); setQuota(q.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const onPick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const ct = f.type || "";
    const isAV = ct.startsWith("audio/") || ct.startsWith("video/");
    if (isAV && !quota?.allow_av) {
      toast.error("Audio/video uploads are a Pro feature. Upgrade to enable.");
      return;
    }
    if (quota && quota.used_bytes + f.size > quota.limit_bytes) {
      toast.error("Storage limit reached. Upgrade your plan to upload more.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      await api.post("/materials", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const del = async (id) => {
    await api.delete(`/materials/${id}`);
    toast.success("Deleted");
    load();
  };

  const download = async (m) => {
    const r = await api.get(`/materials/${m.id}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url; a.download = m.name; a.click();
    URL.revokeObjectURL(url);
  };

  const rename = async (id, newName) => {
    try {
      const r = await api.put(`/materials/${id}`, { name: newName });
      setItems((arr) => arr.map((it) => (it.id === id ? { ...it, name: r.data.name } : it)));
      toast.success("Renamed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't rename.");
      throw e;
    }
  };

  const usedPct = quota ? Math.min(100, (quota.used_bytes / quota.limit_bytes) * 100) : 0;

  return (
    <div className="p-6 sm:p-8 lg:p-12">
      <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Study materials</p>
          <h1 className="font-display text-4xl font-black mt-1">Your study vault</h1>
        </div>
        <label className="nb-btn-primary inline-flex items-center gap-2 cursor-pointer" data-testid="material-upload-label">
          <Upload className="w-4 h-4" strokeWidth={2.5}/> {uploading ? "Uploading…" : "Upload file"}
          <input type="file" className="hidden" onChange={onPick} disabled={uploading} data-testid="material-upload-input"/>
        </label>
      </header>

      <div className="nb-card p-5 mb-6 grid sm:grid-cols-[1fr_auto] gap-4 items-center" style={{ background: "#FDE04733" }}>
        <div>
          <div className="flex justify-between text-xs font-bold mb-1">
            <span data-testid="quota-text">Storage — {fmtBytes(quota?.used_bytes || 0)} of {fmtBytes(quota?.limit_bytes || 0)} ({quota?.plan_label})</span>
            <span>{usedPct.toFixed(0)}%</span>
          </div>
          <div className="h-3 bg-white border-2 border-slate-900 rounded-full overflow-hidden">
            <div className="h-full bg-slate-900 transition-all" style={{ width: `${usedPct}%` }}/>
          </div>
          {quota?.plan === "free" && (
            <p className="text-xs font-medium text-slate-700 mt-2 inline-flex items-center gap-1">
              <Lock className="w-3 h-3" strokeWidth={2.5}/> Audio &amp; video uploads, AI assignment drafts and 4-8&nbsp;GB storage are Pro features.
            </p>
          )}
        </div>
        {quota?.plan === "free" && (
          <Link to="/pricing" data-testid="materials-upgrade-link" className="nb-btn-primary inline-flex items-center gap-2">
            <Sparkles className="w-4 h-4" strokeWidth={2.5}/> Upgrade
          </Link>
        )}
      </div>

      {items.length === 0 ? (
        <div className="nb-card p-12 text-center">
          <Upload className="w-10 h-10 mx-auto" strokeWidth={2.5}/>
          <p className="font-display text-xl font-bold mt-3">Nothing here yet</p>
          <p className="text-sm text-slate-600">Upload notes, PDFs, images — anything you want to keep close.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((m) => (
            <MaterialCard key={m.id} m={m} onDownload={download} onDelete={del} onRename={rename}/>
          ))}
        </div>
      )}
    </div>
  );
}
